import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle, Calendar, Users, Mail } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const FinalCTASection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const benefits = [
    "Configuration en 5 minutes",
    "Support dédié inclus",
    "Conformité garantie",
    "Essai gratuit 30 jours"
  ];

  const handleQuickDemo = async () => {
    if (!email) {
      toast({
        title: "Email requis",
        description: "Veuillez saisir votre email professionnel",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Email invalide",
        description: "Veuillez saisir un email valide",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-demo-request', {
        body: { email }
      });

      if (error) throw error;

      toast({
        title: "Demande envoyée !",
        description: "Nous vous contacterons dans les 2h pour planifier votre démonstration.",
      });
      
      setEmail("");
    } catch (error) {
      console.error('Error sending demo request:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-24 px-4 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          {/* Main heading */}
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-6xl font-bold leading-tight">
              Prêt à révolutionner votre approche du{" "}
              <span className="text-white">télétravail ?</span>
            </h2>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed max-w-3xl mx-auto">
              Rejoignez les entreprises visionnaires qui font confiance à RENEST 
              pour transformer leurs avantages employés et attirer les meilleurs talents.
            </p>
          </div>

          {/* Benefits grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <CheckCircle className="h-5 w-5 text-green-300 flex-shrink-0" />
                <span className="text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-6 shadow-xl group flex-1 bg-white text-primary hover:bg-white/90" 
                asChild
              >
                <Link to="/company-registration">
                  <Users className="mr-2 h-5 w-5" />
                  Créer un compte
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Quick demo form */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Calendar className="h-5 w-5" />
                  <span className="font-semibold">Ou planifiez une démo personnalisée</span>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder="Votre email professionnel"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-white/90 border-white/20 text-foreground placeholder:text-muted-foreground h-12"
                    />
                  </div>
                  <Button 
                    onClick={handleQuickDemo}
                    size="lg"
                    variant="secondary"
                    className="bg-white text-primary hover:bg-white/90 px-6 h-12"
                    disabled={isLoading}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {isLoading ? "Envoi..." : "Planifier une démo"}
                  </Button>
                </div>
                
                <p className="text-sm opacity-75 text-center">
                  Démo de 15 minutes • Sans engagement • Réponse sous 2h
                </p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm opacity-75">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Données sécurisées RGPD</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Support 24/7 inclus</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Intégration en 1 jour</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;